import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { dataverseClient, TABLES } from '@shared/api';
import { assertGuid } from '@shared/lib';
import type { IntakeRecord, IntakeRecordQueryParams, IntakeStatus } from '../model/types';
import { INTAKE_STATUS_VALUES, INTAKE_STATUS_FROM_VALUE } from '../model/types';

// =============================================================================
// Entity Interface
// =============================================================================

interface IntakeRecordEntity {
  vba_salesquestionnaireid: string;
  vba_name: string;
  _vba_opportunity_value?: string;
  _vba_account_value?: string;
  vba_status?: number;
  vba_salesnotes?: string;
  vba_primarycontact?: string;
  vba_contactemail?: string;
  vba_assignedimoid?: string | null;
  vba_assignedimname?: string | null;
  createdon?: string;
  modifiedon?: string;
  [key: string]: unknown;
}

// =============================================================================
// Mappers
// =============================================================================

function mapToDomain(entity: IntakeRecordEntity): IntakeRecord {
  return {
    id: entity.vba_salesquestionnaireid,
    name: entity.vba_name ?? '',
    opportunityId: entity._vba_opportunity_value ?? '',
    accountId: entity._vba_account_value ?? '',
    status: INTAKE_STATUS_FROM_VALUE[entity.vba_status ?? 100_000_000] ?? 'in-review',
    salesNotes: entity.vba_salesnotes ?? undefined,
    primaryContact: entity.vba_primarycontact ?? undefined,
    contactEmail: entity.vba_contactemail ?? undefined,
    assignedImOid: entity.vba_assignedimoid || undefined,
    assignedImName: entity.vba_assignedimname || undefined,
    createdOn: entity.createdon ?? undefined,
    modifiedOn: entity.modifiedon ?? undefined,
  };
}

function mapToEntity(
  input: Partial<IntakeRecord> & { opportunityId?: string; accountId?: string },
): Record<string, unknown> {
  const entity: Record<string, unknown> = {};

  if (input.name !== undefined) entity.vba_name = input.name;
  if (input.status !== undefined) entity.vba_status = INTAKE_STATUS_VALUES[input.status];
  if (input.salesNotes !== undefined) entity.vba_salesnotes = input.salesNotes;
  if (input.primaryContact !== undefined) entity.vba_primarycontact = input.primaryContact;
  if (input.contactEmail !== undefined) entity.vba_contactemail = input.contactEmail;
  if (input.assignedImOid !== undefined) entity.vba_assignedimoid = input.assignedImOid;
  if (input.assignedImName !== undefined) entity.vba_assignedimname = input.assignedImName;

  if (input.opportunityId) {
    entity['vba_Opportunity@odata.bind'] = `/opportunities(${assertGuid(input.opportunityId, 'opportunityId')})`;
  }
  if (input.accountId) {
    entity['vba_Account@odata.bind'] = `/accounts(${assertGuid(input.accountId, 'accountId')})`;
  }

  return entity;
}

// =============================================================================
// API Functions
// =============================================================================

async function list(params: IntakeRecordQueryParams = {}): Promise<IntakeRecord[]> {
  const filters: string[] = ['statecode eq 0'];
  if (params.status) {
    filters.push(`vba_status eq ${INTAKE_STATUS_VALUES[params.status]}`);
  }

  const response = await dataverseClient.list<IntakeRecordEntity>(TABLES.SALES_QUESTIONNAIRE, {
    filter: filters.join(' and '),
    orderBy: 'createdon desc',
  });

  return response.value.map(mapToDomain);
}

async function get(id: string): Promise<IntakeRecord> {
  const entity = await dataverseClient.get<IntakeRecordEntity>(TABLES.SALES_QUESTIONNAIRE, id);
  return mapToDomain(entity);
}

async function create(input: {
  name: string;
  opportunityId: string;
  accountId: string;
  status: IntakeStatus;
  salesNotes?: string;
  assignedImOid?: string | null;
  assignedImName?: string | null;
}): Promise<IntakeRecord> {
  const entity = mapToEntity(input);
  const response = await dataverseClient.create<IntakeRecordEntity>(
    TABLES.SALES_QUESTIONNAIRE,
    entity,
  );
  return mapToDomain(response);
}

async function update(id: string, input: Partial<IntakeRecord>): Promise<void> {
  const entity = mapToEntity(input);
  await dataverseClient.update(TABLES.SALES_QUESTIONNAIRE, id, entity);
}

// =============================================================================
// React Query Hooks
// =============================================================================

const keys = {
  all: ['intakeRecords'] as const,
  lists: () => [...keys.all, 'list'] as const,
  list: (params: IntakeRecordQueryParams) => [...keys.lists(), params] as const,
};

export { keys as intakeRecordKeys };

export function useIntakeRecords(
  params: IntakeRecordQueryParams = {},
  options?: Omit<UseQueryOptions<IntakeRecord[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: keys.list(params),
    queryFn: () => list(params),
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}

export function useCreateIntakeRecord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: create,
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.all }); },
  });
}

export function useUpdateIntakeRecord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<IntakeRecord> }) => update(id, input),
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.all }); },
  });
}
